<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">
            {{ pageTitle }}
          </h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">Cadastre uma nova etiqueta para organizar fluxos e facilitar filtros operacionais.</p>
        </div>

        <button
          type="button"
          class="icon-anim-trigger--back group flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 font-medium text-gray-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
          @click="voltar"
        >
          <svg class="icon-anim icon-anim--back h-5 w-5 text-gray-500 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          Voltar
        </button>
      </div>

      <form class="space-y-6" @submit.prevent="salvarEtiqueta">
        <div class="grid grid-cols-1 gap-6 2xl:grid-cols-2">
          <!-- Bloco Dados da Etiqueta -->
          <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div class="border-b border-gray-100 px-6 py-5">
              <h2 class="text-lg font-semibold text-gray-900">Dados da etiqueta</h2>
              <p class="mt-1 max-w-2xl text-sm text-gray-500">Defina nome, centro de custo e descrição para padronizar o uso em campanhas, suporte e operações.</p>
            </div>

            <div class="p-6">
              <div class="grid grid-cols-1 gap-5">
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-gray-500">Nome*</label>
                  <input v-model="form.nome" type="text" placeholder="Ex: Cobrança Nível 1" :class="inputClass" required />
                </div>

                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-gray-500">Centro de custo*</label>
                  <div class="relative group">
                    <select v-model="form.centrocusto" :class="selectClass" required>
                      <option value="">Selecione</option>
                      <option v-for="centro in centroscusto" :key="centro._id" :value="centro._id">
                        {{ centro.nome }}
                      </option>
                    </select>
                    <svg
                      class="select-icon pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 transition-all duration-200 group-focus-within:text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-gray-500">Descrição</label>
                  <textarea v-model="form.descricao" rows="5" placeholder="Descreva quando essa etiqueta deve ser aplicada." :class="textareaClass" />
                </div>

                <label class="flex items-center gap-3 rounded-xl border border-gray-200 bg-slate-50/40 px-4 py-3 text-sm text-gray-700">
                  <input v-model="form.ativo" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  Etiqueta ativa para uso imediato
                </label>
              </div>
            </div>
          </div>

          <!-- Bloco Configurações Avançadas -->
          <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div class="border-b border-gray-100 px-6 py-5">
              <h2 class="text-lg font-semibold text-gray-900">Configurações avançadas</h2>
              <p class="mt-1 max-w-2xl text-sm text-gray-500">Opções adicionais para padronizar o comportamento da etiqueta.</p>
            </div>

            <div class="p-6 space-y-4">
              <label class="flex items-start gap-3 rounded-xl border border-gray-200 bg-slate-50/40 px-4 py-3 text-sm text-gray-700 transition hover:border-gray-300 hover:bg-slate-50">
                <input v-model="form.etiquetaParaFinalizarConversas" type="checkbox" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span class="flex flex-col">
                  <span class="font-medium text-gray-800">Usar etiqueta para finalizar conversas</span>
                  <span class="text-gray-500 text-xs mt-1">Ative quando esta etiqueta representar o fechamento de um atendimento.</span>
                </span>
              </label>

              <label class="flex items-start gap-3 rounded-xl border border-gray-200 bg-slate-50/40 px-4 py-3 text-sm text-gray-700 transition hover:border-gray-300 hover:bg-slate-50">
                <input v-model="form.editarCorDaEtiqueta" type="checkbox" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span class="flex flex-col">
                  <span class="font-medium text-gray-800">Personalizar cor da etiqueta</span>
                  <span class="text-gray-500 text-xs mt-1">Ajuste a cor quando houver necessidade operacional clara.</span>
                </span>
              </label>

              <div v-if="form.editarCorDaEtiqueta" class="mt-4 rounded-xl border border-gray-200 bg-slate-50/40 p-4">
                <label class="block mb-3 text-sm font-medium text-gray-700">Cor da etiqueta</label>
                <div class="flex flex-col gap-3">
                  <input
                    v-model="form.corDaEtiqueta"
                    type="text"
                    placeholder="#000000"
                    class="h-11 rounded-xl border border-gray-200 bg-white px-4 text-gray-700 outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                  />
                  <input v-model="form.corDaEtiqueta" type="color" class="w-full h-12 rounded-xl border border-gray-200 cursor-pointer" />
                  <div class="flex items-center gap-2 text-xs text-gray-600">
                    <div class="h-6 w-6 rounded border border-gray-200" :style="{ backgroundColor: form.corDaEtiqueta }"></div>
                    <span>{{ form.corDaEtiqueta }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="feedback.message" class="rounded-2xl border px-4 py-3 text-sm" :class="feedbackClass">
          {{ feedback.message }}
        </div>

        <div class="flex justify-end">
          <button
            type="submit"
            class="h-11 rounded-xl px-6 font-medium text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            :style="{ backgroundColor: primaryColor }"
            @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
            @mouseleave="$event.target.style.backgroundColor = primaryColor"
          >
            Salvar etiqueta
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { colorMixin } from "@/utils/colorMixin";

export default {
  mixins: [colorMixin],
  data() {
    return {
      centroscusto: [],
      feedback: {
        message: "",
        type: "success"
      },
      form: {
        _id: null,
        ativo: true,
        nome: "",
        descricao: "",
        corDaEtiqueta: "#000000",
        centrocusto: null,
        etiquetaParaFinalizarConversas: false,
        editarCorDaEtiqueta: false
      },
      inputClass: "h-11 rounded-xl border border-gray-200 bg-white px-4 text-gray-700 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100",
      selectClass:
        "h-11 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white bg-none px-4 pr-10 text-gray-700 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100",
      textareaClass: "w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-gray-700 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
    };
  },
  computed: {
    pageTitle() {
      return this.form._id ? "Editar Etiqueta" : "Nova Etiqueta";
    },

    feedbackClass() {
      const feedbackStyles = {
        success: "border-green-200 bg-green-50 text-green-700",
        warning: "border-amber-200 bg-amber-50 text-amber-700"
      };

      return feedbackStyles[this.feedback.type] || feedbackStyles.success;
    }
  },
  methods: {
    mostrarFeedback(message, type = "success") {
      this.feedback.message = message;
      this.feedback.type = type;
    },
    voltar() {
      this.$router.push({ name: "Tag" });
    },
    normalizeForm() {
      this.form.nome = String(this.form.nome || "").trim();
      this.form.descricao = String(this.form.descricao || "").trim();
      this.form.ativo = !!this.form.ativo;
      this.form.etiquetaParaFinalizarConversas = !!this.form.etiquetaParaFinalizarConversas;
      this.form.editarCorDaEtiqueta = !!this.form.editarCorDaEtiqueta;
    },
    async salvarEtiqueta() {
      if (!this.form.nome.trim()) {
        this.mostrarFeedback("Preencha o nome da etiqueta para continuar.", "warning");
        return;
      }

      if (!this.form.centrocusto) {
        this.mostrarFeedback("Selecione um centro de custo para continuar.", "warning");
        return;
      }

      this.normalizeForm();

      const method = this.form._id ? "put" : "post";
      const response = await this.$http[method]("/v1/tag", this.form);

      if (response && response.data && response.data.success) {
        this.$vToastify.success("Salvo com sucesso!");
        this.$router.push({ name: "Tag" });
        return;
      }

      const errorMessage = response && response.data && response.data.err ? response.data.err : "Não foi possível salvar a etiqueta.";
      this.mostrarFeedback(errorMessage, "warning");
      this.$vToastify.error(errorMessage);
    },
    async carregarEtiqueta() {
      const id = this.$route.params.id;

      if (!id) {
        return;
      }

      const response = await this.$http.get(`/v1/tag/${id}`);
      const etiqueta = response && response.data ? response.data : null;

      if (!etiqueta) {
        return;
      }

      this.form = {
        _id: etiqueta._id || null,
        ativo: typeof etiqueta.ativo === "boolean" ? etiqueta.ativo : true,
        nome: etiqueta.nome || "",
        descricao: etiqueta.descricao || "",
        corDaEtiqueta: etiqueta.corDaEtiqueta || "#000000",
        centrocusto: etiqueta.centrocusto && etiqueta.centrocusto._id ? etiqueta.centrocusto._id : etiqueta.centrocusto || null,
        etiquetaParaFinalizarConversas: !!etiqueta.etiquetaParaFinalizarConversas,
        editarCorDaEtiqueta: !!etiqueta.editarCorDaEtiqueta
      };
    },
    async carregarCentrosDeCusto() {
      const response = await this.$http.post("/v1/centrocusto/list", { all: true });
      this.centroscusto = response && response.data && response.data.data ? response.data.data : [];
    }
  },
  async beforeMount() {
    await this.carregarEtiqueta();
    await this.carregarCentrosDeCusto();
  }
};
</script>

<style scoped>
.icon-anim {
  transition: transform 0.2s ease;
}

.icon-anim-trigger--back:hover .icon-anim--back,
.icon-anim-trigger--back:focus-visible .icon-anim--back {
  transform: translateX(-4px);
}

.select-icon {
  pointer-events: none;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

.group:focus-within .select-icon {
  transform: translateY(-50%) rotate(180deg);
}
</style>
